import { formatDateBR, isIsoDate } from "./dates";
import { formatDocument, normalizeDocument } from "./documents";
import { DomainError } from "./errors";
import { formatBRL } from "./money";
import { formatPct } from "./pct";
import { formatQty } from "./qty";

/**
 * Construtor de relatórios customizados (T3.6, RF-CAD-06). O usuário nunca escreve SQL: a definição é declarativa
 * (fonte, colunas, filtros, agrupamento e ordenação, tudo por chave) e este compilador a traduz em SELECT com
 * parâmetros sobre um CATÁLOGO montado pelo servidor (DO): cada fonte já traz a empresa (e a carteira do
 * usuário-vendedor) no WHERE e só as colunas que o perfil vê. Expressões SQL vêm só do catálogo; valores do usuário
 * só entram como parâmetros. Coluna que existe mas a permissão esconde = 403 `report_column_forbidden`.
 */

export const REPORT_SOURCES = [
  "sales_orders",
  "sales_order_items",
  "products",
  "stock",
  "contacts",
  "receivables",
  "payables",
] as const;
export type ReportSource = (typeof REPORT_SOURCES)[number];
export const REPORT_SOURCE_LABELS: Record<ReportSource, string> = {
  sales_orders: "Pedidos de venda",
  sales_order_items: "Itens de pedidos de venda",
  products: "Produtos",
  stock: "Saldos de estoque por depósito",
  contacts: "Clientes e fornecedores",
  receivables: "Contas a receber",
  payables: "Contas a pagar",
};

export const REPORT_COLUMN_TYPES = [
  "text",
  "enum",
  "document",
  "int",
  "money",
  "qty",
  "pct",
  "date",
  "datetime",
  "bool",
] as const;
export type ReportColumnType = (typeof REPORT_COLUMN_TYPES)[number];
export const REPORT_AGGS = ["sum", "count", "min", "max"] as const;
export type ReportAgg = (typeof REPORT_AGGS)[number];
export const REPORT_AGG_LABELS: Record<ReportAgg, string> = {
  sum: "Soma",
  count: "Quantidade",
  min: "Mínimo",
  max: "Máximo",
};
export const REPORT_OPS = [
  "eq",
  "neq",
  "contains",
  "starts_with",
  "in",
  "gt",
  "gte",
  "lt",
  "lte",
  "between",
  "empty",
  "not_empty",
] as const;
export type ReportOp = (typeof REPORT_OPS)[number];
export const REPORT_OP_LABELS: Record<ReportOp, string> = {
  eq: "igual a",
  neq: "diferente de",
  contains: "contém",
  starts_with: "começa com",
  in: "é um de",
  gt: "maior que",
  gte: "a partir de",
  lt: "menor que",
  lte: "até",
  between: "entre",
  empty: "está vazio",
  not_empty: "está preenchido",
};

const NUM_OPS = ["eq", "neq", "gt", "gte", "lt", "lte", "between", "empty", "not_empty"] as const;
const DATE_OPS = ["eq", "gte", "lte", "between", "empty", "not_empty"] as const;
export const REPORT_OPS_BY_TYPE: Record<ReportColumnType, readonly ReportOp[]> = {
  text: ["eq", "neq", "contains", "starts_with", "in", "empty", "not_empty"],
  document: ["eq", "starts_with", "empty", "not_empty"],
  enum: ["eq", "neq", "in", "empty", "not_empty"],
  int: NUM_OPS,
  money: NUM_OPS,
  qty: NUM_OPS,
  pct: NUM_OPS,
  date: DATE_OPS,
  datetime: DATE_OPS,
  bool: ["eq"],
};
export const REPORT_AGGS_BY_TYPE: Record<ReportColumnType, readonly ReportAgg[]> = {
  text: ["count"],
  document: ["count"],
  enum: ["count"],
  bool: ["count"],
  int: ["sum", "count", "min", "max"],
  money: ["sum", "count", "min", "max"],
  qty: ["sum", "count", "min", "max"],
  pct: ["count", "min", "max"],
  date: ["count", "min", "max"],
  datetime: ["count", "min", "max"],
};

export const REPORT_MAX_COLUMNS = 30;
export const REPORT_MAX_FILTERS = 20;
export const REPORT_MAX_IN_VALUES = 50;
export const REPORT_MAX_TEXT = 200;
export const REPORT_MAX_GROUP = 3;
export const REPORT_MAX_SORT = 3;
/** Linhas da prévia na tela. */
export const REPORT_PREVIEW_ROWS = 100;
/** A contagem para em 100.001 ("mais de 100.000"). */
export const REPORT_COUNT_CAP = 100_000;
export const REPORT_EXPORT_MAX_ROWS = 50_000;
export const REPORT_PDF_MAX_ROWS = 2_000;
/** Página de leitura da exportação (RPC ao DO). */
export const REPORT_EXPORT_PAGE = 1_000;
/** Modelos salvos por empresa (arquivados não contam). */
export const MAX_CUSTOM_REPORTS = 200;

export type ReportFilterValue = string | number | boolean;
export type ReportColumnRef = { key: string; agg?: ReportAgg | null };
export type ReportFilter = {
  key: string;
  op: ReportOp;
  value?: ReportFilterValue | null;
  values?: ReportFilterValue[];
};
export type ReportSort = { key: string; agg?: ReportAgg | null; dir: "asc" | "desc" };
export type ReportDefinition = {
  source: ReportSource;
  columns: ReportColumnRef[];
  filters: ReportFilter[];
  groupBy: string[];
  sort: ReportSort[];
};

export type SqlParam = string | number;
export type ReportColumnSpec = {
  key: string;
  label: string;
  type: ReportColumnType;
  /** Expressão SQL do servidor (nunca do usuário). */
  expr: string;
  /** Lista fechada: valor gravado → rótulo. */
  options?: Readonly<Record<string, string>>;
};
export type ReportSourceSpec = {
  key: ReportSource;
  label: string;
  /** FROM do servidor; `fromParams` são os `?` dele (antes dos do WHERE). */
  from: string;
  fromParams?: readonly SqlParam[];
  where: readonly string[];
  params: readonly SqlParam[];
  /** Desempate estável da listagem (chave única por linha). */
  order: readonly string[];
  columns: readonly ReportColumnSpec[];
  /** Colunas que existem mas o perfil não vê: chave → rótulo (para a mensagem do 403). */
  hidden?: Readonly<Record<string, string>>;
};
export type ReportCatalog = Partial<Record<ReportSource, ReportSourceSpec>>;
export type ReportOutputColumn = {
  key: string;
  agg: ReportAgg | null;
  label: string;
  type: ReportColumnType;
  options: Readonly<Record<string, string>> | undefined;
};
export type CompiledReport = {
  sql: string;
  /** O mesmo SELECT sem o ORDER BY (a contagem não precisa ordenar). */
  unorderedSql: string;
  params: SqlParam[];
  columns: ReportOutputColumn[];
  aggregated: boolean;
};

const invalid = (msg: string): never => {
  throw new DomainError("invalid_report", msg);
};

/** Rótulo de saída da coluna ("Soma de Total"). */
export const reportColumnLabel = (label: string, agg: ReportAgg | null | undefined): string =>
  !agg ? label : agg === "count" ? "Quantidade de registros" : `${REPORT_AGG_LABELS[agg]} de ${label}`;

function lookup(src: ReportSourceSpec, key: string): ReportColumnSpec {
  const c = src.columns.find((x) => x.key === key);
  if (c) return c;
  const hidden = src.hidden && Object.hasOwn(src.hidden, key) ? src.hidden[key] : undefined;
  if (hidden !== undefined)
    throw new DomainError(
      "report_column_forbidden",
      `Este relatório usa colunas que o seu perfil não pode ver: ${hidden}.`,
    );
  return invalid(`Coluna desconhecida: ${key}.`);
}

const NUMERIC: ReadonlySet<ReportColumnType> = new Set(["int", "money", "qty", "pct"]);
const DATES: ReadonlySet<ReportColumnType> = new Set(["date", "datetime"]);

/** Valor do filtro conferido pelo tipo da coluna e convertido para o parâmetro SQL. */
function param(c: ReportColumnSpec, v: ReportFilterValue | null | undefined): SqlParam {
  if (v === undefined || v === null) return invalid(`Informe o valor do filtro "${c.label}".`);
  if (NUMERIC.has(c.type)) {
    if (typeof v !== "number" || !Number.isSafeInteger(v))
      return invalid(`O filtro "${c.label}" espera um número inteiro (centavos, q4 ou pct4).`);
    return v;
  }
  if (DATES.has(c.type)) {
    if (typeof v !== "string" || !isIsoDate(v)) return invalid(`O filtro "${c.label}" espera uma data.`);
    return v;
  }
  if (c.type === "bool") {
    if (typeof v !== "boolean") return invalid(`O filtro "${c.label}" espera sim ou não.`);
    return v ? 1 : 0;
  }
  if (typeof v !== "string" || v.length > REPORT_MAX_TEXT)
    return invalid(`O filtro "${c.label}" espera um texto de até ${REPORT_MAX_TEXT} caracteres.`);
  if (c.type === "enum" && !(c.options && Object.hasOwn(c.options, v)))
    return invalid(`Valor inválido no filtro "${c.label}".`);
  if (c.type === "document") return normalizeDocument(v);
  return v;
}

function filterSql(c: ReportColumnSpec, f: ReportFilter, params: SqlParam[]): string {
  if (!REPORT_OPS_BY_TYPE[c.type].includes(f.op))
    return invalid(`O filtro "${REPORT_OP_LABELS[f.op]}" não vale para "${c.label}".`);
  // Data com hora: compara só o dia (aaaa-mm-dd).
  const e = c.type === "datetime" ? `substr(${c.expr}, 1, 10)` : c.expr;
  const textual = !NUMERIC.has(c.type) && c.type !== "bool";
  switch (f.op) {
    case "empty":
      return textual ? `(${c.expr} IS NULL OR ${c.expr} = '')` : `${c.expr} IS NULL`;
    case "not_empty":
      return textual ? `(${c.expr} IS NOT NULL AND ${c.expr} <> '')` : `${c.expr} IS NOT NULL`;
    case "in": {
      const vs = f.values ?? [];
      if (vs.length < 1 || vs.length > REPORT_MAX_IN_VALUES)
        return invalid(`Escolha de 1 a ${REPORT_MAX_IN_VALUES} valores no filtro "${c.label}".`);
      for (const v of vs) params.push(param(c, v));
      return `${e} IN (${vs.map(() => "?").join(", ")})`;
    }
    case "between": {
      const vs = f.values ?? [];
      if (vs.length !== 2) return invalid(`Informe o início e o fim do filtro "${c.label}".`);
      params.push(param(c, vs[0]), param(c, vs[1]));
      return `${e} BETWEEN ? AND ?`;
    }
    case "contains":
      params.push(param(c, f.value));
      return `instr(lower(${e}), lower(?)) > 0`;
    case "starts_with": {
      const v = param(c, f.value) as string;
      params.push(v.length, v);
      return `substr(lower(${e}), 1, ?) = lower(?)`;
    }
    case "neq":
      params.push(param(c, f.value));
      return `(${e} IS NULL OR ${e} <> ?)`;
    default: {
      params.push(param(c, f.value));
      const op = { eq: "=", gt: ">", gte: ">=", lt: "<", lte: "<=" }[f.op];
      return `${e} ${op} ?`;
    }
  }
}

const sameCol = (a: ReportColumnRef, b: ReportColumnRef) =>
  a.key === b.key && (a.agg ?? null) === (b.agg ?? null);

/** Traduz a definição em SELECT parametrizado sobre o catálogo (sem LIMIT; use `reportPageSql`). */
export function compileReport(def: ReportDefinition, catalog: ReportCatalog): CompiledReport {
  const src = Object.hasOwn(catalog, def.source) ? catalog[def.source] : undefined;
  if (!src)
    throw new DomainError(
      "report_source_forbidden",
      "O seu perfil não pode ver os dados desta fonte de relatório.",
    );
  if (def.columns.length < 1 || def.columns.length > REPORT_MAX_COLUMNS)
    invalid(`Escolha de 1 a ${REPORT_MAX_COLUMNS} colunas.`);
  if (def.filters.length > REPORT_MAX_FILTERS) invalid(`Use até ${REPORT_MAX_FILTERS} filtros.`);
  if (def.groupBy.length > REPORT_MAX_GROUP) invalid(`Agrupe por até ${REPORT_MAX_GROUP} colunas.`);
  if (def.sort.length > REPORT_MAX_SORT) invalid(`Ordene por até ${REPORT_MAX_SORT} colunas.`);

  const aggregated = def.groupBy.length > 0 || def.columns.some((c) => !!c.agg);
  const groupSpecs = def.groupBy.map((k) => lookup(src, k));
  if (new Set(def.groupBy).size !== def.groupBy.length) invalid("Coluna repetida no agrupamento.");

  const columns: ReportOutputColumn[] = [];
  const select: string[] = [];
  def.columns.forEach((ref, i) => {
    const c = lookup(src, ref.key);
    const agg = ref.agg ?? null;
    if (def.columns.findIndex((o) => sameCol(o, ref)) !== i) invalid(`Coluna repetida: ${c.label}.`);
    if (agg && !REPORT_AGGS_BY_TYPE[c.type].includes(agg))
      invalid(`"${REPORT_AGG_LABELS[agg]}" não vale para "${c.label}".`);
    if (aggregated && !agg && !def.groupBy.includes(ref.key))
      invalid(
        `"${c.label}" precisa estar no agrupamento ou ter um resumo (soma, quantidade, mínimo ou máximo).`,
      );
    const expr = agg === "count" ? "COUNT(*)" : agg ? `${agg.toUpperCase()}(${c.expr})` : c.expr;
    select.push(`${expr} AS c${i}`);
    columns.push({
      key: c.key,
      agg,
      label: reportColumnLabel(c.label, agg),
      type: agg === "count" ? "int" : c.type,
      options: agg === "count" ? undefined : c.options,
    });
  });
  for (const k of def.groupBy)
    if (!def.columns.some((c) => c.key === k && !c.agg))
      invalid(`A coluna "${lookup(src, k).label}" do agrupamento precisa estar entre as colunas.`);

  const params: SqlParam[] = [...(src.fromParams ?? []), ...src.params];
  const where = [...src.where];
  for (const f of def.filters) where.push(filterSql(lookup(src, f.key), f, params));

  const order: string[] = [];
  for (const s of def.sort) {
    lookup(src, s.key);
    const idx = def.columns.findIndex((c) => sameCol(c, { key: s.key, agg: s.agg ?? null }));
    if (idx < 0) invalid("Ordene só por colunas escolhidas no relatório.");
    order.push(`c${idx} ${s.dir === "desc" ? "DESC" : "ASC"}`);
  }
  // Desempate estável (paginação da exportação): chave única da fonte, ou as colunas do agrupamento.
  if (aggregated) order.push(...groupSpecs.map((g) => g.expr));
  else order.push(...src.order);

  let sql = `SELECT ${select.join(", ")} FROM ${src.from}`;
  if (where.length) sql += ` WHERE ${where.join(" AND ")}`;
  if (groupSpecs.length) sql += ` GROUP BY ${groupSpecs.map((g) => g.expr).join(", ")}`;
  const unorderedSql = sql;
  if (order.length) sql += ` ORDER BY ${order.join(", ")}`;
  return { sql, unorderedSql, params, columns, aggregated };
}

export const reportPageSql = (r: CompiledReport, limit: number, offset: number) => ({
  sql: `${r.sql} LIMIT ? OFFSET ?`,
  params: [...r.params, limit, offset],
});
/**
 * Conta as linhas até o teto (`REPORT_COUNT_CAP + 1` = "mais de"), sem o ORDER BY: ordenar não muda a contagem e,
 * numa coluna sem índice, obrigaria o SQLite a ordenar a fonte inteira antes do LIMIT (revisão de segurança).
 */
export const reportCountSql = (r: CompiledReport) => ({
  sql: `SELECT COUNT(*) FROM (${r.unorderedSql} LIMIT ?)`,
  params: [...r.params, REPORT_COUNT_CAP + 1],
});

const INT_FMT = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 });
const DATETIME_FMT = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/** Texto da célula em pt-BR (tela e PDF). Valor fora do tipo vira o texto cru. */
export function reportCellText(
  col: { type: ReportColumnType; options?: Readonly<Record<string, string>> },
  v: unknown,
): string {
  if (v === null || v === undefined || v === "") return "";
  const n = typeof v === "number" && Number.isSafeInteger(v) ? v : null;
  switch (col.type) {
    case "money":
      return n === null ? String(v) : formatBRL(n);
    case "qty":
      return n === null ? String(v) : formatQty(n, 0);
    case "pct":
      return n === null ? String(v) : formatPct(n);
    case "int":
      return n === null ? String(v) : INT_FMT.format(n);
    case "bool":
      return v === 1 || v === true ? "Sim" : "Não";
    case "date":
      return typeof v === "string" && isIsoDate(v.slice(0, 10)) ? formatDateBR(v.slice(0, 10)) : String(v);
    case "datetime": {
      const t = typeof v === "string" ? Date.parse(v) : NaN;
      return Number.isNaN(t) ? String(v) : DATETIME_FMT.format(new Date(t)).replace(",", "");
    }
    case "enum":
      return col.options && typeof v === "string" && Object.hasOwn(col.options, v)
        ? col.options[v]!
        : String(v);
    case "document":
      return typeof v === "string" ? formatDocument(v) : String(v);
    default:
      return String(v);
  }
}

/** Modelos prontos (os relatórios fixos do PRD) em cima do construtor. */
export type ReportPreset = { id: string; name: string; description: string; definition: ReportDefinition };
const p = (
  id: string,
  name: string,
  description: string,
  d: Omit<ReportDefinition, "filters"> & { filters?: ReportFilter[] },
): ReportPreset => ({ id, name, description, definition: { filters: [], ...d } });
export const REPORT_PRESETS: readonly ReportPreset[] = [
  p("vendas-periodo", "Vendas por período (dia)", "Pedidos, itens e faturamento por dia.", {
    source: "sales_orders",
    columns: [
      { key: "data" },
      { key: "numero", agg: "count" },
      { key: "total", agg: "sum" },
      { key: "frete", agg: "sum" },
    ],
    groupBy: ["data"],
    sort: [{ key: "data", dir: "asc" }],
  }),
  p("vendas-cliente", "Vendas por cliente", "Pedidos e faturamento por cliente, do maior para o menor.", {
    source: "sales_orders",
    columns: [{ key: "cliente" }, { key: "numero", agg: "count" }, { key: "total", agg: "sum" }],
    groupBy: ["cliente"],
    sort: [{ key: "total", agg: "sum", dir: "desc" }],
  }),
  p("vendas-produto", "Vendas por produto", "Quantidade e receita por produto vendido.", {
    source: "sales_order_items",
    columns: [
      { key: "codigo" },
      { key: "produto" },
      { key: "quantidade", agg: "sum" },
      { key: "total", agg: "sum" },
    ],
    groupBy: ["codigo", "produto"],
    sort: [{ key: "total", agg: "sum", dir: "desc" }],
  }),
  p("estoque-valorizado", "Estoque valorizado", "Saldo físico × custo médio por produto e depósito.", {
    source: "stock",
    columns: [
      { key: "sku" },
      { key: "produto" },
      { key: "deposito" },
      { key: "fisico" },
      { key: "custo_medio" },
      { key: "valor" },
    ],
    groupBy: [],
    sort: [{ key: "produto", dir: "asc" }],
  }),
  p("receber-vencimento", "Contas a receber por vencimento", "Em aberto por data de vencimento.", {
    source: "receivables",
    columns: [{ key: "vencimento" }, { key: "descricao", agg: "count" }, { key: "em_aberto", agg: "sum" }],
    filters: [{ key: "situacao", op: "in", values: ["open", "partial"] }],
    groupBy: ["vencimento"],
    sort: [{ key: "vencimento", dir: "asc" }],
  }),
  p("pagar-vencimento", "Contas a pagar por vencimento", "Em aberto por data de vencimento.", {
    source: "payables",
    columns: [{ key: "vencimento" }, { key: "descricao", agg: "count" }, { key: "em_aberto", agg: "sum" }],
    filters: [{ key: "situacao", op: "in", values: ["open", "partial"] }],
    groupBy: ["vencimento"],
    sort: [{ key: "vencimento", dir: "asc" }],
  }),
  p("comissoes-vendedor", "Comissões por vendedor", "Total vendido e comissão calculada nos pedidos.", {
    source: "sales_orders",
    columns: [{ key: "vendedor" }, { key: "total", agg: "sum" }, { key: "comissao", agg: "sum" }],
    groupBy: ["vendedor"],
    sort: [{ key: "comissao", agg: "sum", dir: "desc" }],
  }),
];
